<script lang="ts" setup>
import type { Component } from 'vue'
import { computed, useTemplateRef, watch } from 'vue'
import FloatLabel from 'primevue/floatlabel'
import Button from 'primevue/button'
import Panel from 'primevue/panel'
import { Form, type FormSubmitEvent } from '@primevue/forms'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { ZodType } from 'zod'
import type { FormInstance } from '@primevue/forms'
import isUndefined from 'lodash/isUndefined'
import debounce from 'lodash/debounce'

interface filterModel {
  header: string
  field: string
  component: Component
  placeholder?: string
  containerClass?: string
  zod: ZodType<unknown, unknown>
  props?: Record<string, unknown>
}

const props = defineProps<{
  filters: Array<filterModel>
  zod: ZodType
  loading: boolean
  gridClasses: string
  panelClass: string
  name?: string
  disableRefreshButton?: boolean
  panelProps?: object | null
  inited: boolean
  complexFields?: string[]
  formData?: Record<string, unknown>
  initValues?: Record<string, unknown>
  formSubmit: (event: FormSubmitEvent<Record<string, unknown>>) => void
}>()

const filtersForm = useTemplateRef<FormInstance>('filtersForm')

function isEveryValueNullOrEmptyArray(
  obj: Record<string, unknown>,
  fields: string[] | null = null,
): boolean {
  const entriesToCheck =
    fields === null
      ? Object.entries(obj)
      : Object.entries(obj).filter(([key]) => !fields.includes(key))

  return entriesToCheck.every(
    ([, value]) => value === null || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && value.trim() === ''),
  )
}

const currentData = computed(() => {
  const res = {} as Record<string, unknown>
  const entries = Object.entries(filtersForm.value?.states ?? {})
  for (const [key, value] of entries) {
    res[key] = value.value
  }
  return res
})


const currentDataChangeDebounced = debounce(() => {
  filtersForm.value?.submit()
}, 1000)

watch(
  () => props.formData,
  (v) => {
    if (!v) return
    const entries = Object.entries(v)
    for (const [k, v] of entries) {
      if (!isUndefined(filtersForm.value?.states[k])) {
        filtersForm.value.states[k].value = v
      }
    }
  },
  { deep: true },
)
watch(currentData, () => {
  if (!props.inited) {
    return
  }
  currentDataChangeDebounced()
})
const filtersFilled = computed(
  () => !isEveryValueNullOrEmptyArray(currentData.value, props.complexFields),
)
const formName = computed(() => {
  return props.name ?? 'filters-form'
})
</script>

<template>
  <!-- toggleable -->
  <Panel :class="props.panelClass" :collapsed="false" v-bind="props.panelProps" pt:header:class="py-2 px-0">
    <template #header>
      <div class="ms-2">
        <slot name="top-left"></slot>
      </div>
    </template>
    <template #toggleicon>
      <i class="pi" :class="{ 'pi-filter-fill': filtersFilled, 'pi-filter': !filtersFilled }"></i>
    </template>
    <template #icons>
      <div class="gap-2 flex me-4 mt-2 mb-2">
        <slot name="actions"></slot>
        <Button v-if="!disableRefreshButton" size="small" icon="pi pi-refresh" :loading="loading" type="submit"
          :disabled="loading" severity="secondary" rounded :form="formName" />
      </div>
    </template>
    <Form ref="filtersForm" :id="formName" :initialValues="props.initValues" @submit="formSubmit"
      :resolver="zodResolver(props.zod)">
      <div class="mt-1 grid items-end" :class="props.gridClasses">
        <div v-for="filter in props.filters" :key="filter.field" :class="filter.containerClass">
          <FloatLabel variant="on">
            <component :is="filter.component" :id="filter.field" :name="filter.field" :placeholder="filter.placeholder"
              v-bind="filter.props" :loading="loading" fluid>
            </component>
            <label :for="filter.field">{{ $t(`filters.${filter.header}`) }}</label>
          </FloatLabel>
        </div>
      </div>
    </Form>
  </Panel>
</template>
